import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';

import { BroadcastService } from '../../core/services/broadcast.service';
import { CachedCollection } from '../common/cached-collection';
import { Child } from './child.model';
import { ChildEditModel } from './child-edit.model';
import { ChildrenApiService } from './children-api.service';
import { ChildrenCountWithCategoriesResult } from './children-count-with-categories-result.model';
import { ServiceManagerBase } from '../common/service-manager-base';

@Injectable({
    providedIn: 'root'
})
export class ChildrenManagerService extends ServiceManagerBase
{
    // Cache Containers
    private _childrenCollection?: CachedCollection<Child> = undefined;

    constructor(private childrenApiService: ChildrenApiService,
                protected override broadcastService: BroadcastService)
    {
        super(broadcastService);
    }

    clearCache()
    {
        if (this.retrievingData) return;
        this._childrenCollection = undefined;
    }

    getChildren(bypassCache = false): Observable<Child[]>
    {
        if (!bypassCache && this._childrenCollection && !this._childrenCollection.isExpired)
        {
            return of(this._childrenCollection.collection);
        }

        this.retrievingData = true;
        return this.childrenApiService.getChildren().pipe(tap(response =>
        {
            this._childrenCollection = new CachedCollection(response);
            this.retrievingData = false;
        }));
    }

    getChildrenWithFaceRecognitionResults(): Observable<Child[]>
    {
        return this.childrenApiService.getChildrenWithFaceRecognitionResults();
    }

    getChild(childId: number): Observable<Child>
    {
        return this.childrenApiService.getChild(childId);
    }

    getChildWithFaceRecognitionResults(childId: number): Observable<Child>
    {
        return this.childrenApiService.getChildWithFaceRecognitionResults(childId);
    }

    saveChild(child: ChildEditModel): Observable<Child>
    {
        if (child.Id === 0) return this.childrenApiService.createChild(child);
        return this.childrenApiService.updateChild(child).pipe(tap(updatedChild => {
            const item: Child = this._childrenCollection?.collection.find(child => child.Id === updatedChild.Id)!;
            if (item === undefined) this._childrenCollection?.collection.push(updatedChild);
            else Object.assign(item, updatedChild);
        }));
    }

    deleteChildren(childrenIds: number[]): Observable<any>
    {
        return this.childrenApiService.deleteChildren(childrenIds)
            .pipe(tap(() =>
                {
                    if (this._childrenCollection === undefined) return;
                    childrenIds.forEach(id => this._childrenCollection!.removeItem(this._childrenCollection!.collection.find(m => m.Id === id)!));
                })
            );
    }

    getChildrenCountForCategories(categoryIds: number[]): Observable<ChildrenCountWithCategoriesResult>
    {
        return this.childrenApiService.getChildrenCountForCategories(categoryIds);
    }

    searchChildren(searchString: string): Observable<Child[]>
    {
        const lower = searchString.toLowerCase();
        return this.getChildren().pipe(switchMap(children => of(children.filter(c => c.fullName.toLowerCase().includes(lower)))));
    }

    addCategoriesForChildren(childrenIds: number[], categoryIds: number[], legalCategoryIdsForChildren: number[]): Observable<any>
    {
        return this.childrenApiService.addCategoriesForChildren(childrenIds, categoryIds).pipe(tap(() =>
            {
                if (this._childrenCollection === undefined) return;
                childrenIds.forEach(childId =>
                {
                    const child = this._childrenCollection!.collection.find(m => m.Id === childId)!;
                    categoryIds.filter(categoryId => legalCategoryIdsForChildren.includes(categoryId) && !child.CategoryIds.includes(categoryId))
                        .forEach(categoryId => child.CategoryIds.push(categoryId));
                });
            })
        );
    }

    deleteCategoriesForChildren(childrenIds: number[], categoryIds: number[]): Observable<any>
    {
        return this.childrenApiService.deleteCategoriesForChildren(childrenIds, categoryIds).pipe(tap(() =>
            {
                if (this._childrenCollection === undefined) return;
                childrenIds.forEach(childId =>
                {
                    const child = this._childrenCollection!.collection.find(m => m.Id === childId)!;
                    child.CategoryIds = child.CategoryIds.filter(existingCategoryId => !categoryIds.includes(existingCategoryId));
                });
            })
        );
    }
}
